/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : MindMapPage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";

import CustomFilterV2 from "../../components/customComponentsV2/common/filter/CustomFilterV2";
import CustomCheckBoxV2 from "../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";


import VlcMindMap from "./MindMap";
import VlcZonalMindMap from "./ZonalMindMap";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import ProjectVariantSearchbar from "../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import ProjectVariantDialog from "../../components/customComponentsV2/vlc/ProjectVarientAccordian/ProjectVariantDialog";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";

function MindMapPage() {

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails
  );
  const selectedSingleVariantDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails
  );
  const projectDetailsMindMap = useSelector(
    (state) => state?.labCarReducer?.projectDetailsMindMap
  );



  const [openLegendDialog, setOpenLegendDialog] = useState(false);

  const networkInterfacesFromApi = useMemo(() => {
    const interfacesMap = new Map();

    // Collect from EE components
    if (Array.isArray(projectDetailsMindMap?.ee_details)) {
      projectDetailsMindMap.ee_details.forEach((ee) => {
        if (Array.isArray(ee?.linked_ee_ids)) {
          ee.linked_ee_ids.forEach((item) => {
            const name = item?.network_interface;
            if (
              name &&
              name.toLowerCase() !== "network not defined" &&
              !interfacesMap.has(name)
            ) {
              interfacesMap.set(name, item.color || "#000000");
            }
          });
        }
      });
    }

    // Collect from SWM modules
    if (Array.isArray(projectDetailsMindMap?.swm_details)) {
      projectDetailsMindMap.swm_details.forEach((swm) => {
        if (Array.isArray(swm?.linked_swm_ids)) {
          swm.linked_swm_ids.forEach((item) => {
            const name = item?.network_interface;
            if (
              name &&
              name.toLowerCase() !== "network not defined" &&
              !interfacesMap.has(name)
            ) {
              interfacesMap.set(name, item.color || "#000000");
            }
          });
        }
      });
    }

    return Array.from(interfacesMap.entries()).map(([name, color]) => ({
      name,
      color,
    }));
  }, [projectDetailsMindMap]);

  const allNetworkInterfaces = networkInterfacesFromApi;

  const initialFilterState = useMemo(
    () => ({
      type: {
        all: false,
        ee_component: false,
        software_module: false,
      },
      network_interfaces: [],
      network_interfaces_all: false,
    }),
    []
  );

  const [appliedFilters, setAppliedFilters] = useState(initialFilterState);
  const [draftFilters, setDraftFilters] = useState(initialFilterState);
  const [anchorFilterEl, setAnchorFilterEl] = useState(null);
  const [networkSearchText, setNetworkSearchText] = useState("");

  const isFilterOpen = Boolean(anchorFilterEl);

  const handleFilterClick = (event) => {
    setAnchorFilterEl(event.currentTarget);
    setDraftFilters(appliedFilters);
    setNetworkSearchText("");
  };

  const handleFilterClose = () => {
    setAnchorFilterEl(null);
    setDraftFilters(appliedFilters);
    setNetworkSearchText("");
  };

  const handleFilterAction = (action) => {
    switch (action) {
      case "discard":
        setDraftFilters(initialFilterState);
        setNetworkSearchText("");
        break;

      case "cancel":
        setDraftFilters(appliedFilters);
        setNetworkSearchText("");
        setAnchorFilterEl(null);
        break;

      case "apply":
        setAppliedFilters(draftFilters);
        setAnchorFilterEl(null);
        break;

      default:
        break;
    }
  };

  const handleResetFilters = () => {
    setAppliedFilters(initialFilterState);
    setDraftFilters(initialFilterState);
    setNetworkSearchText("");
  };

  const filteredNetworkInterfaces = useMemo(() => {
    if (!networkSearchText.trim()) return allNetworkInterfaces;
    const query = networkSearchText.trim().toLowerCase();
    return allNetworkInterfaces.filter((item) =>
      item.name.toLowerCase().includes(query)
    );
  }, [allNetworkInterfaces, networkSearchText]);

  const filterByTypeContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
      <CustomCheckBoxV2
        label="ALL"
        value={draftFilters.type.all}
        onChange={(e) => {
          const checked = e.target.checked;
          setDraftFilters((prev) => ({
            ...prev,
            type: {
              all: checked,
              ee_component: checked,
              software_module: checked,
            },
          }));
        }}
      />
      <CustomCheckBoxV2
        label="EE Component"
        value={draftFilters.type.ee_component}
        onChange={(e) => {
          const checked = e.target.checked;
          setDraftFilters((prev) => {
            const nextEe = checked;
            const nextSwm = prev.type.software_module;
            return {
              ...prev,
              type: {
                all: nextEe && nextSwm,
                ee_component: nextEe,
                software_module: nextSwm,
              },
            };
          });
        }}
      />
      <CustomCheckBoxV2
        label="Software Module"
        value={draftFilters.type.software_module}
        onChange={(e) => {
          const checked = e.target.checked;
          setDraftFilters((prev) => {
            const nextEe = prev.type.ee_component;
            const nextSwm = checked;
            return {
              ...prev,
              type: {
                all: nextEe && nextSwm,
                ee_component: nextEe,
                software_module: nextSwm,
              },
            };
          });
        }}
      />
    </Box>
  );

  const filterByNetworkContent = (
    <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search network interface"
        value={networkSearchText}
        onChange={(e) => setNetworkSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: "18px", color: "#63666A" }} />
            </InputAdornment>
          ),
          endAdornment: networkSearchText ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setNetworkSearchText("")}
                sx={{ p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 0,
            height: "32px",
            fontSize: "13px",
            backgroundColor: "#fff",
          },
        }}
      />

      <CustomCheckBoxV2
        label="ALL"
        value={draftFilters.network_interfaces_all}
        onChange={(e) => {
          const checked = e.target.checked;
          setDraftFilters((prev) => ({
            ...prev,
            network_interfaces_all: checked,
            network_interfaces: checked
              ? allNetworkInterfaces.map((item) => item.name)
              : [],
          }));
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          maxHeight: "220px",
          overflowY: "auto",
          pr: 0.5,
          mt: 0.5,
        }}
      >
        {filteredNetworkInterfaces.map((item) => {
          const isChecked = draftFilters.network_interfaces.includes(item.name);
          return (
            <Box
              key={item.name}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <CustomCheckBoxV2
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: item.color || "#000000",
                        borderRadius: "2px",
                        border: "1px solid rgba(0, 0, 0, 0.2)",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#333",
                        fontWeight: 500,
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Box>
                }
                value={isChecked}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setDraftFilters((prev) => {
                    const updated = checked
                      ? [...prev.network_interfaces, item.name]
                      : prev.network_interfaces.filter((n) => n !== item.name);
                    const allSelected =
                      allNetworkInterfaces.length > 0 &&
                      updated.length === allNetworkInterfaces.length;
                    return {
                      ...prev,
                      network_interfaces: updated,
                      network_interfaces_all: allSelected,
                    };
                  });
                }}
              />
            </Box>
          );
        })}
        {filteredNetworkInterfaces.length === 0 && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontStyle: "italic", mt: 1 }}
          >
            No network interfaces found
          </Typography>
        )}
      </Box>
    </Box>
  );

  const filterSections = [
    {
      heading: "Filter By Type",
      content: filterByTypeContent,
    },
    {
      heading: "Filter By Network Interface",
      content: filterByNetworkContent,
    },
  ];

  const canRenderMap =
    selectedProjectDetails && selectedSingleVariantDetails?.variant_id;

  const breadcrumbItems = [{ label: "Virtual Lab-Car" }, { label: "Mind Map" }];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "12px 16px", md: "12px 24px" },
          flexShrink: 0,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          <CustomFilterV2
            anchorEl={anchorFilterEl}
            open={isFilterOpen}
            widthPerColumn="260px"
            handleClick={handleFilterClick}
            handleClose={handleFilterClose}
            handleFilterButton={handleFilterAction}
            showClearAll={true}
            clearBtnName="Clear"
            buttonSx={{
              height: "32px",
            }}
            sections={filterSections}
          />
          <ProjectVariantSearchbar />
          <ProjectVariantDialog />
        </Box>

        {networkInterfacesFromApi.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              ml: "auto",
              p: "6px 12px",
              backgroundColor: "#fff",
              borderRadius: "6px",
              border: "1px solid #e0e0e0",
            }}
          >
            {networkInterfacesFromApi.slice(0, 3).map((item) => (
              <Box
                key={item.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: item.color || "#000000",
                    borderRadius: "2px",
                    border: "1px solid rgba(0, 0, 0, 0.2)",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  {item.name}
                </Typography>
              </Box>
            ))}

            {networkInterfacesFromApi.length > 3 && (
              <Typography
                variant="caption"
                onClick={() => setOpenLegendDialog(true)}
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#1976d2",
                  cursor: "pointer",
                  textDecoration: "underline",
                  ml: 0.5,
                  "&:hover": {
                    color: "#115293",
                  },
                }}
              >
                View More
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <CustomDialogBoxV2
        open={openLegendDialog}
        onClose={() => setOpenLegendDialog(false)}
        title="Legends"
        paperSx={{ width: "320px", maxWidth: "90vw" }}
        dialogActions={
          <CustomButtonV2
            type="cancel"
            btnName="Close"
            handleOnClick={() => setOpenLegendDialog(false)}
          />
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            py: 1,
          }}
        >
          {networkInterfacesFromApi.map((item) => (
            <Box
              key={item.name}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: "16px",
                    height: "16px",
                    backgroundColor: item.color || "#000000",
                    borderRadius: "2px",
                    border: "1px solid rgba(0, 0, 0, 0.2)",
                    flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#333",
                  wordBreak: "break-word",
                }}
              >
                {item.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </CustomDialogBoxV2>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          padding: { xs: "8px", md: "12px" },
        }}
      >
        {canRenderMap &&
          (selectedProjectDetails?.is_zonal ? (
            <VlcZonalMindMap
              isDialog={true}
              selectedProjectId={selectedProjectDetails?.project_id}
              selectedVariantId={selectedSingleVariantDetails?.variant_id}
              filters={appliedFilters}
              onResetFilters={handleResetFilters}
            />
          ) : (
            <VlcMindMap
              selectedProjectId={selectedProjectDetails?.project_id}
              selectedVariantId={selectedSingleVariantDetails?.variant_id}
              filters={appliedFilters}
              onResetFilters={handleResetFilters}
            />
          ))}
      </Box>
    </Box>
  );
}

export default MindMapPage;
